import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { FileText, Upload, Download, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { sanitizeFilename, validateUpload, safeContentType } from '@/lib/sanitizeFilename';

const BUCKET = 'cohort-reports';

interface Props {
  cohortId: string;
  isSuperAdmin: boolean;
  reportPath: string | null;
  reportName: string | null;
  uploadedAt: string | null;
}

export function CohortResultsReportCard({ cohortId, isSuperAdmin, reportPath, reportName, uploadedAt }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ['cohort-management', 'cohorts'] });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const check = validateUpload(file, { maxBytes: 50 * 1024 * 1024 });
    if (!check.ok) {
      toast.error(check.error);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setBusy(true);
    try {
      const path = `${cohortId}/${Date.now()}_${sanitizeFilename(file.name)}`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: safeContentType(file),
      });
      if (upErr) throw upErr;

      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('cohorts')
        .update({
          results_report_url: path,
          results_report_name: file.name,
          results_report_uploaded_at: new Date().toISOString(),
          results_report_uploaded_by: auth.user?.id ?? null,
        } as any)
        .eq('id', cohortId);
      if (error) throw error;

      if (reportPath) await supabase.storage.from(BUCKET).remove([reportPath]);
      toast.success('Informe de resultados cargado');
      refresh();
    } catch (err: any) {
      toast.error('No se pudo subir el informe: ' + (err.message ?? ''));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    if (!reportPath) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(reportPath, 300, {
        download: reportName ?? true,
      });
      if (error || !data?.signedUrl) throw error ?? new Error('Sin enlace');
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      toast.error('No se pudo descargar el informe: ' + (err.message ?? ''));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!reportPath) return;
    setBusy(true);
    try {
      await supabase.storage.from(BUCKET).remove([reportPath]);
      const { error } = await supabase
        .from('cohorts')
        .update({
          results_report_url: null,
          results_report_name: null,
          results_report_uploaded_at: null,
          results_report_uploaded_by: null,
        } as any)
        .eq('id', cohortId);
      if (error) throw error;
      toast.success('Informe eliminado');
      refresh();
    } catch (err: any) {
      toast.error('No se pudo eliminar el informe: ' + (err.message ?? ''));
    } finally {
      setBusy(false);
    }
  };

  if (!isSuperAdmin && !reportPath) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Resultados</CardTitle>
          <CardDescription>Aún no hay informe de resultados.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resultados</CardTitle>
        <CardDescription>
          {isSuperAdmin
            ? 'Informe de resultados de la cohorte. Visible para el administrador de la empresa.'
            : 'Informe de resultados de la cohorte.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {reportPath && (
          <div className="flex items-center gap-3 rounded-md border border-border p-3">
            <FileText className="w-5 h-5 text-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{reportName || 'Informe de resultados'}</p>
              {uploadedAt && (
                <p className="text-xs text-muted-foreground">
                  Cargado el {format(new Date(uploadedAt), "d 'de' MMMM yyyy", { locale: es })}
                </p>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handleDownload} disabled={busy} className="gap-2">
              <Download className="w-4 h-4" /> Descargar
            </Button>
            {isSuperAdmin && (
              <Button variant="ghost" size="icon" onClick={handleRemove} disabled={busy} title="Eliminar informe">
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            )}
          </div>
        )}

        {isSuperAdmin && (
          <div>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()} className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {reportPath ? 'Reemplazar informe' : 'Subir informe'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
              className="hidden"
              onChange={handleUpload}
            />
            <p className="text-xs text-muted-foreground mt-2">PDF, Word, PowerPoint o Excel (máx. 50 MB).</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
